import { useMemo } from "react"
import { useDimensions } from "./../dimensions"
import {
    useGetConfiguracionesPanelQuery,
    TiposAlojamientos,
    useConfiguracionEstanciaQuery,
} from "src/gql/schema"
import { useProfile } from "src/shared/hooks/useProfile"
import { useFormatDate } from "src/shared/hooks/useFormatDate"
import {
    Medidas,
    days,
    getCapitalizeText,
    getCardsPorPagina,
    getEscalaCards,
    getHoras,
    getItems,
    getLabelCamas,
    getOrderUrls,
} from "./utils"
import { capitalizeString } from "src/shared/hooks/capitalizeString"
import { DisponibilidadHabitacion, useDisponibilidad } from "../../providers/DisponibilidadProvider"

type Tarifa = NonNullable<DisponibilidadHabitacion["tarifas"]>[number]

type Tipo = DisponibilidadHabitacion & {
    tarifa_noches: Tarifa
    tarifa_horas: Tarifa
}

// `area` es el tamaño medido del contenedor de las tarjetas (ver `useCardArea`).
export function useData(area: Medidas) {
    const { height, width } = useDimensions()
    const { hotel_id, logo_hotel } = useProfile()
    const { nowHotel } = useFormatDate()
    const { loading, habitaciones, listaEspera, folioTurno, folioAnuncio } = useDisponibilidad()

    const { data: config } = useGetConfiguracionesPanelQuery({ variables: { hotel_id } })

    const { data: config_estancia } = useConfiguracionEstanciaQuery({ variables: { hotel_id } })

    const configurations = config?.hotel.configurations
    const configuration_noche = config_estancia?.configuracion_estancia.data.configuracion_noches

    const getTarifa = (tarifa: Tarifa) => {
        const day = days[nowHotel.getDay()]
        return {
            ...(tarifa || {}),
            costo_habitacion: Number(tarifa?.dias_disponibles?.[day] || 0)
        }
    }

    const itemList = useMemo(() => {
        const items: Tipo[] = []

        const array = [...new Map(habitaciones.map((obj) => [obj.tipo_habitacion_id, obj])).values()]


        array.forEach((item) => {
            const tarifa_horas = item.tarifas?.find((i)=>i.tipo_alojamiento === TiposAlojamientos.Motel) as Tarifa
            const tarifa_noches = item.tarifas?.find((i)=>i.tipo_alojamiento === TiposAlojamientos.Hotel) as Tarifa

            if(Number(item?.tarifas?.length || 0) > 0) {
                items.push({
                    ...item,
                    tarifa_noches: getTarifa(tarifa_noches),
                    tarifa_horas: getTarifa(tarifa_horas),
                })
            }
        })
        return items
    }, [habitaciones])

    const viewport = useMemo(() => ({ width, height }), [width, height])
    const escalaCards = getEscalaCards(area, viewport)

    const turnosListaEspera = useMemo(
        () => getItems(listaEspera, getCardsPorPagina(area, viewport, true)),
        [listaEspera, area, viewport]
    )

    const items = useMemo(
        () => getItems(
            itemList?.map((d) => {
                const personas_extra = Number(d?.tarifa_noches?.tipo_habitacion?.personas_extra_max || d?.tarifa_horas?.tipo_habitacion?.personas_extra_max || 0)
                const costo_persona_extra = Number(d?.tarifa_noches?.costo_persona_extra || d?.tarifa_horas?.costo_persona_extra || 0)
                return {
                    title: getCapitalizeText(d?.nombre || "Sin nombre"),
                    image: d?.foto || "",
                    hours: d?.tarifa_horas?.grupo_tarifa?.duracion || getHoras(configuration_noche) || 0,
                    size: d?.personas_incluidas || 0,
                    disponibles: d?.disponibles || 0,
                    price: d?.tarifa_horas?.costo_habitacion || d?.tarifa_noches?.costo_habitacion || 0,
                    priceBooking: d?.tarifa_noches?.costo_habitacion > 0 ? d?.tarifa_noches?.costo_habitacion : null,
                    priceExtra: personas_extra > 0 && costo_persona_extra > 0 ? costo_persona_extra : null,
                    maxExtra: personas_extra,
                    amenidades: d?.amenidades?.length > 0 ? capitalizeString(d?.amenidades?.join(", ")) : "",
                    camas: getLabelCamas(d?.camas || []),
                    duracionReserva: getHoras(configuration_noche),
                    numeroCamas: Number(d?.camas?.length || 0)
                }
            }),
            getCardsPorPagina(area, viewport)
        ),
        [itemList, area, viewport, configuration_noche]
    )

    return {
        logo: logo_hotel || "",
        loading,
        items,
        escala_cards: escalaCards,
        img_promo_horizontal: configurations?.panel?.imagen_promo?.horizontal?.length
            ? getOrderUrls(configurations.panel.imagen_promo.horizontal)
            : [],
        img_promo_vertical: configurations?.panel?.imagen_promo?.vertical?.length
            ? getOrderUrls(configurations.panel.imagen_promo.vertical)
            : [],
        lista_espera: turnosListaEspera,
        folio_turn: folioTurno,
        folio_anuncio: folioAnuncio,
        avisos: configurations?.panel?.avisos || [],
        pie_pagina: configurations?.panel?.pie_pagina || "",
    }
}
